import { useMemo } from "react"
import { ModalidadGrupoTarifa, ReservaTarifa } from "src/gql/schema"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { DesgloseGrupoTarifa, RegistroReservaTarifa } from "../useMotorTarifas/useDesgloseReservasTarifas.type"

const MODALIDAD_PRIORIDAD: Partial<Record<ModalidadGrupoTarifa, number>> = {
    [ModalidadGrupoTarifa.Adicional]: 1,
    [ModalidadGrupoTarifa.Temporada]: 2,
    [ModalidadGrupoTarifa.Base]: 3,
}

const MODALIDAD_DISPLAY_ORDER: Partial<Record<ModalidadGrupoTarifa, number>> = {
    [ModalidadGrupoTarifa.Base]: 1,
    [ModalidadGrupoTarifa.Temporada]: 2,
    [ModalidadGrupoTarifa.Adicional]: 3,
}

function getPrioridadModalidad(modalidad: ModalidadGrupoTarifa | null): number {
    return (modalidad !== null && modalidad !== undefined && MODALIDAD_PRIORIDAD[modalidad]) || 3
}

type DateInterval = { start: string; end: string; endIsCut?: boolean }

function resolveAvailableRanges(baseRange: DateInterval, occupiedRange: DateInterval): DateInterval[] {
    if (occupiedRange.end <= baseRange.start || occupiedRange.start >= baseRange.end) {
        return [baseRange]
    }
    const result: DateInterval[] = []
    if (baseRange.start < occupiedRange.start) {
        result.push({ start: baseRange.start, end: occupiedRange.start, endIsCut: true })
    }
    if (occupiedRange.end < baseRange.end) {
        result.push({ start: occupiedRange.end, end: baseRange.end, endIsCut: baseRange.endIsCut })
    }
    return result
}

export function useDesgloseReservasTarifas(
    reservas_tarifas: ReservaTarifa[] | null | undefined,
) {
    const { formatCustomDate } = useFormatDate()

    const getFormattedDateRange = (startDate: string, endDate: string, nights: number): string => {
        const inicio = formatCustomDate(startDate.split("T")[0], "MMM, DD")
        const fin = formatCustomDate(endDate.split("T")[0], "MMM, DD")
        return nights <= 1 ? inicio : `${inicio} - ${fin}`
    }

    const desgloseReservasTarifas = useMemo((): DesgloseGrupoTarifa[] => {
        if (!reservas_tarifas?.length) return []

        const gruposTarifas: Record<string, Omit<DesgloseGrupoTarifa, "registros_finales">> = {}

        for (const reservaTarifa of reservas_tarifas) {
            const grupoTarifaId = reservaTarifa.tarifa?.grupo_tarifa_id ?? reservaTarifa.tarifa_id
            const nights = reservaTarifa.subtotales.noches ?? 1
            const rango = getFormattedDateRange(reservaTarifa.fecha_inicio, reservaTarifa.fecha_fin, nights)

            if (!gruposTarifas[grupoTarifaId]) {
                gruposTarifas[grupoTarifaId] = {
                    grupo_tarifa_id: grupoTarifaId,
                    nombre: reservaTarifa.tarifa?.grupo_tarifa?.nombre ?? reservaTarifa.tarifa?.nombre ?? "",
                    modalidad: (reservaTarifa.tarifa?.grupo_tarifa?.modalidad as ModalidadGrupoTarifa) ?? null,
                    subtotal: 0,
                    dias: 0,
                    registros: [],
                }
            }

            const registro: RegistroReservaTarifa = {
                fecha_inicio: reservaTarifa.fecha_inicio,
                fecha_fin: reservaTarifa.fecha_fin,
                noches: nights,
                precio_unitario: reservaTarifa.subtotales.precio_unitario,
                total: reservaTarifa.subtotales.total,
                rango,
                costo_persona_extra: reservaTarifa.tarifa?.costo_persona_extra ?? 0,
            }

            gruposTarifas[grupoTarifaId].subtotal += reservaTarifa.subtotales.total
            gruposTarifas[grupoTarifaId].dias += nights
            gruposTarifas[grupoTarifaId].registros.push(registro)
        }

        // Normaliza intervalos a partir de fecha_inicio + noches
        const intervalsByPrioridad = new Map<number, DateInterval[]>()
        for (const grupo of Object.values(gruposTarifas)) {
            const priority = getPrioridadModalidad(grupo.modalidad)
            if (!intervalsByPrioridad.has(priority)) intervalsByPrioridad.set(priority, [])
            const groupIntervals = intervalsByPrioridad.get(priority) ?? []
            for (const registro of grupo.registros) {
                const fin = new Date(registro.fecha_inicio)
                fin.setUTCDate(fin.getUTCDate() + registro.noches)
                groupIntervals.push({ start: registro.fecha_inicio, end: fin.toISOString() })
            }
        }

        return Object.values(gruposTarifas).map((grupo): DesgloseGrupoTarifa => {
            const priority = getPrioridadModalidad(grupo.modalidad)

            const intervaloOcupados: DateInterval[] = []
            for (const [p, intervals] of intervalsByPrioridad) {
                if (p < priority) intervaloOcupados.push(...intervals)
            }

            if (intervaloOcupados.length === 0) {
                return { ...grupo, registros_finales: grupo.registros }
            }

            const registros_finales: RegistroReservaTarifa[] = []

            for (const registro of grupo.registros) {
                const fin = new Date(registro.fecha_inicio)
                fin.setUTCDate(fin.getUTCDate() + registro.noches)

                let intervalosDisponibles: DateInterval[] = [
                    { start: registro.fecha_inicio, end: fin.toISOString() },
                ]

                for (const occupiedRange of intervaloOcupados) {
                    intervalosDisponibles = intervalosDisponibles.flatMap((baseRange) => resolveAvailableRanges(baseRange, occupiedRange))
                }

                for (const baseRange of intervalosDisponibles) {
                    const nights = Math.round(
                        (new Date(baseRange.end).getTime() - new Date(baseRange.start).getTime()) / 86400000
                    )
                    if (nights <= 0) continue

                    // Ajusta la fecha final visible cuando el baseRange fue recortado
                    let displayFin = baseRange.end
                    if (baseRange.endIsCut) {
                        const d = new Date(baseRange.end)
                        d.setUTCDate(d.getUTCDate() - 1)
                        displayFin = d.toISOString()
                    }

                    registros_finales.push({
                        ...registro,
                        fecha_inicio: baseRange.start,
                        fecha_fin: baseRange.end,
                        noches: nights,
                        rango: getFormattedDateRange(baseRange.start, displayFin, nights),
                    })
                }
            }

            return { ...grupo, registros_finales }
        }).sort((a, b) => {
            const orderA = (a.modalidad && MODALIDAD_DISPLAY_ORDER[a.modalidad]) ?? 99
            const orderB = (b.modalidad && MODALIDAD_DISPLAY_ORDER[b.modalidad]) ?? 99
            return orderA - orderB
        })
    }, [reservas_tarifas, formatCustomDate])

    return { desglose_tarifas_reservas: desgloseReservasTarifas }
}
